import { COMPASS, COMPASS_LOOKS, FRAMES, resolveColour, type CompassSlot } from "odyssey-shared";
import { COMPASS_GLYPHS, COMPASS_GLYPH_SIZE, type CompassLetter } from "./compass-glyphs.ts";
import { COMPASS_SHAPE_SIZE, paintShape, pipFill } from "./compass-shapes.ts";

/**
 * The compass band's painter (P1.9-T187; ADR-061's amendment of 2026-10-14, "a square compass round the minimap",
 * decisions 1 to 3). The band is 7 logical pixels wide round the 70 by 70 field, inside the HUD frame's inset, so the
 * slot is 90 square. The painter draws into a canvas as large as the frame's inside, 84 square: the `ink-600` band
 * (`surface`), the 1 px `ink-900` rule (`frame-edge`) round the field, the letters N, E, S and W at the middle of each
 * edge, aligned on the player's pixel, and a 2 by 2 tick in each corner for the diagonals. North is the one letter in
 * `text`; the other three and the four ticks are in `text-muted`. A letter or a tick is not drawn while a pip's 7 by 7
 * box overlaps it, so a pip never leaves a sliver of one. The pips (P1.9-T188) are drawn over the band from the pip
 * buffer, lowest in the draw order first so that the top one lies over the rest, each a 5 by 5 core in its colour and a
 * 1 px outline (`compass-shapes.ts`) centred on its place on the middle line. It draws at one canvas pixel to a logical pixel, so the canvas
 * is scaled by whole device pixels and stays sharp.
 */

/**
 * The part of a 2D context the painter uses.
 */
export interface BandContext {
  fillStyle: string | CanvasGradient | CanvasPattern;
  fillRect(x: number, y: number, width: number, height: number): void;
}

/**
 * A pip as the painter needs it: the centre of its place on the band's middle line, in the slot's logical pixels (the
 * `x` and `y` of a `CompassPip`).
 */
export interface BandPip {
  readonly x: number;
  readonly y: number;
}

/**
 * The side of a pip's box in pixels: its 5 by 5 core and the 1 px outline round it.
 */
export const COMPASS_PIP_BOX = 7;

/**
 * The side of a corner tick in pixels.
 */
export const COMPASS_TICK_SIZE = 2;

/**
 * Where the slot's pixels start inside the frame: the HUD frame's inset, which the band's canvas starts after.
 */
export const COMPASS_INSET = FRAMES.hud.insets.left;

/**
 * The canvas's side in logical pixels: the slot less the frame's inset on both sides.
 */
export const COMPASS_CANVAS = COMPASS.slot - 2 * COMPASS_INSET;

/**
 * One mark of the band: a letter or a tick, as a rectangle in the slot's pixels and the colour role it is drawn in.
 */
export interface BandMark {
  readonly name: string;
  readonly left: number;
  readonly top: number;
  readonly size: number;
  readonly letter: CompassLetter | null;
  readonly role: "text" | "text-muted";
}

/**
 * The band's eight marks in the slot's pixels: the letters 5 by 5 centred on the player's pixel along each edge and on
 * the middle line across it, and the ticks 2 by 2 at the four corners of the line.
 *
 * @returns The marks, the four letters first (N, E, S, W) then the ticks (NE, SE, SW, NW).
 */
function bandMarks(): readonly BandMark[] {
  const half = (COMPASS_GLYPH_SIZE - 1) / 2;
  const near = COMPASS.lineMin - half;
  const far = COMPASS.lineMax - half;
  const along = COMPASS.player - half;
  const tickNear = COMPASS.lineMin - 1;
  const tickFar = COMPASS.lineMax;
  return Object.freeze([
    { name: "N", left: along, top: near, size: COMPASS_GLYPH_SIZE, letter: "N", role: "text" },
    { name: "E", left: far, top: along, size: COMPASS_GLYPH_SIZE, letter: "E", role: "text-muted" },
    { name: "S", left: along, top: far, size: COMPASS_GLYPH_SIZE, letter: "S", role: "text-muted" },
    { name: "W", left: near, top: along, size: COMPASS_GLYPH_SIZE, letter: "W", role: "text-muted" },
    { name: "NE", left: tickFar, top: tickNear, size: COMPASS_TICK_SIZE, letter: null, role: "text-muted" },
    { name: "SE", left: tickFar, top: tickFar, size: COMPASS_TICK_SIZE, letter: null, role: "text-muted" },
    { name: "SW", left: tickNear, top: tickFar, size: COMPASS_TICK_SIZE, letter: null, role: "text-muted" },
    { name: "NW", left: tickNear, top: tickNear, size: COMPASS_TICK_SIZE, letter: null, role: "text-muted" },
  ] as const satisfies readonly BandMark[]);
}

/**
 * The band's eight marks.
 */
export const BAND_MARKS: readonly BandMark[] = bandMarks();

/**
 * Whether a pip's box overlaps a mark.
 *
 * @param mark The letter or tick.
 * @param pip The pip, by the centre of its place in the slot's pixels.
 * @returns True when the two rectangles share a pixel.
 */
function covered(mark: BandMark, pip: BandPip): boolean {
  const reach = (COMPASS_PIP_BOX - 1) / 2;
  return pip.x - reach < mark.left + mark.size && mark.left < pip.x + reach + 1 && pip.y - reach < mark.top + mark.size && mark.top < pip.y + reach + 1;
}

/**
 * The marks the band draws with these pips on it: every letter and tick whose rectangle no pip's box overlaps.
 *
 * @param pips The pips on the band, by the centre of their places in the slot's pixels.
 * @param count How many of `pips` are in use, all of them when omitted.
 * @returns The marks to draw, in the order of `BAND_MARKS`.
 */
export function visibleMarks(pips: readonly BandPip[], count: number = pips.length): readonly BandMark[] {
  return BAND_MARKS.filter((mark) => {
    for (let at = 0; at < count; at += 1) {
      if (covered(mark, pips[at] as BandPip)) {
        return false;
      }
    }
    return true;
  });
}

const SURFACE = resolveColour("surface");
const RULE = resolveColour("frame-edge");
const ROLE_COLOURS: Readonly<Record<BandMark["role"], string>> = { text: resolveColour("text"), "text-muted": resolveColour("text-muted") };

/**
 * Paints the band: the `ink-600` ground across the canvas, the 1 px `ink-900` rule round the field's edge, then the
 * letters and ticks no pip covers. The field's own canvas lies over the middle, so the ground there is hidden. Called
 * once, and again when the slot's size changes and the canvas is drawn afresh.
 *
 * @param context The band canvas's 2D context, `COMPASS_CANVAS` square.
 * @param pips The pips on the band, so that no letter or tick shows under one (`paintPips` draws them).
 * @param count How many of `pips` are in use, all of them when omitted.
 */
export function paintCompass(context: BandContext, pips: readonly BandPip[] = [], count: number = pips.length): void {
  const rule = COMPASS.band - 1;
  const ruleSide = COMPASS.field + 2;
  context.fillStyle = SURFACE;
  context.fillRect(0, 0, COMPASS_CANVAS, COMPASS_CANVAS);
  context.fillStyle = RULE;
  context.fillRect(rule, rule, ruleSide, 1);
  context.fillRect(rule, rule + ruleSide - 1, ruleSide, 1);
  context.fillRect(rule, rule + 1, 1, ruleSide - 2);
  context.fillRect(rule + ruleSide - 1, rule + 1, 1, ruleSide - 2);
  for (const mark of visibleMarks(pips, count)) {
    context.fillStyle = ROLE_COLOURS[mark.role];
    const left = mark.left - COMPASS_INSET;
    const top = mark.top - COMPASS_INSET;
    if (mark.letter === null) {
      context.fillRect(left, top, mark.size, mark.size);
      continue;
    }
    COMPASS_GLYPHS[mark.letter].forEach((row, y) => {
      let x = 0;
      while (x < row.length) {
        if (row[x] !== "#") {
          x += 1;
          continue;
        }
        let end = x;
        while (end < row.length && row[end] === "#") {
          end += 1;
        }
        context.fillRect(left + x, top + y, end - x, 1);
        x = end;
      }
    });
  }
}

/**
 * Paints the pips over the band: lowest in the draw order first, so the top one is over the rest, each a shape of
 * `COMPASS_LOOKS` in its colour with its 1 px `ink-900` outline, its 7 by 7 box centred on its place.
 *
 * @param context The band canvas's 2D context, `COMPASS_CANVAS` square, already painted by `paintCompass`.
 * @param slots The pip buffer's slots, the top pip first.
 * @param count How many of `slots` are in use.
 */
export function paintPips(context: BandContext, slots: readonly CompassSlot[], count: number): void {
  const reach = (COMPASS_SHAPE_SIZE - 1) / 2;
  for (let at = count - 1; at >= 0; at -= 1) {
    const slot = slots[at] as CompassSlot;
    const look = COMPASS_LOOKS[slot.kind];
    paintShape(context, look.shape, slot.x - reach - COMPASS_INSET, slot.y - reach - COMPASS_INSET, pipFill(slot.kind, slot.place), resolveColour(look.outline));
  }
}
